import type { GroupLifeMember } from '@/features/quotes/context/group-life-quote.context';
import { useGroupLifeQuote } from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { GROUP_LIFE_USE_ASYNC_IMPORT } from '@/features/quotes/data/group-life/feature-flags';
import { useAsyncMembersImport } from '@/features/quotes/hooks/group-life/use-async-import.hook';
import { useCalculateGroupLifeQuote } from '@/features/quotes/hooks/group-life/use-calculate-group-life-quote.hook';
import { useCnaeLabel } from '@/features/quotes/hooks/group-life/use-cnae-search.hook';
import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import {
    membersPageBucketKey,
    useMembersPage,
} from '@/features/quotes/hooks/group-life/use-members-page.hook';
import { useSaveGroupLifeStep } from '@/features/quotes/hooks/group-life/use-save-step.hook';
import { normalizeHydratedCoverageSelections } from '@/features/quotes/utils/group-life/coverage-hydration';
import { resolveAll } from '@/features/quotes/utils/group-life/coverage-resolver';
import {
    recallMemberCpf,
    rememberMemberCpf,
} from '@/features/quotes/utils/group-life/cpf-cache';
import { resolveMemberDisplayCapital } from '@/features/quotes/utils/group-life/effective-capital';
import {
  formatPercentValue,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import { groupLifeService } from '@/services';
import type {
    GroupLifeDuplicateCpfsPayload,
    GroupLifeMembersPageResponse,
} from '@/services/group-life';
import { toaster } from '@/shared/ui/toaster.component';
import { Button, Select, Text } from '@noorden-insurtech/alba-design-system';
import { useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft,
    ArrowUpDown,
    Building,
    Building2,
    Calculator,
    ChevronLeft,
    ChevronRight,
    Download,
    Loader2,
    MoreVertical,
    Pencil,
    Plus,
    Search,
    Trash2,
    Upload,
    UserPlus,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AddMemberModal } from './add-member.modal';
import { AddSubStipulatorModal } from './add-sub-stipulator.modal';
import { AsyncImportOverlay } from './async-import-overlay.component';
import { DuplicateMembersDialog } from './duplicate-members.dialog';
import { ImportMembersModal } from './import-members.modal';
import { InsurableGroupLabel } from './insurable-group-label.component';

type Props = {
  onNext: () => void;
  onBack: () => void;
  isViewMode?: boolean;
};

/**
 * Special target ID used by the modals when the user is operating on the
 * main Stipulator (vs. one of the SubStipulators which carry their own
 * uuid).
 */
const STIPULATOR_TARGET = '__STIPULATOR__';

type StatusFilter = 'ALL' | 'OK' | 'WARNING';

type MemberStatus = 'OK' | 'WARNING';

function memberStatus(m: GroupLifeMember): MemberStatus {
  // "Pendente" só quando a vida está AFASTADA (awayFromWork) e NENHUM CID foi
  // informado — um afastamento sem CID está incompleto. Afastada COM CID, ou
  // não-afastada, fica OK.
  const hasCid = (m.cids ?? []).some(c => (c ?? '').trim() !== '');
  return m.awayFromWork && !hasCid ? 'WARNING' : 'OK';
}


export function StepRisk({ onNext, onBack, isViewMode = false }: Props) {
  const memberUniqueIdsRef = useRef(new Map<string, string>());
  const {
    stipulator,
    stipulatorMembers,
    subStipulators,
    addSubStipulator,
    removeSubStipulator,
    removeMemberFromSubStipulator,
    bulkAddMembersToSubStipulator,
    removeMemberFromStipulator,
    bulkAddMembersToStipulator,
    updateMemberInStipulator,
    updateMemberInSubStipulator,
    clearAllMembers,
    isCpfRegistered,
    coverageSchema,
    setCoverageSchema,
    plan,
    setPlan,
  } = useGroupLifeQuote();
  const { policyId, productCode } = useQuoteParams();

  // Garante o catálogo de coberturas no Step 3 também. O schema só é populado
  // quando o Step 2 monta; ao recarregar (F5) direto no Step 3 ele ficaria
  // vazio e a sub-tabela de coberturas da vida não renderizaria. Carregamos
  // aqui e sincronizamos ao contexto quando ainda não há schema.
  const coverageSchemaQuery = useGroupLifeCoverageSchema({
    productCode: productCode || undefined,
  });
  useEffect(() => {
    if (coverageSchemaQuery.data && !coverageSchema) {
      setCoverageSchema(coverageSchemaQuery.data);
    }
  }, [coverageSchemaQuery.data, coverageSchema, setCoverageSchema]);

  // Plano/coberturas do Step 2 — necessário para a sub-tabela (Morte, MA, etc.).
  useEffect(() => {
    if (!coverageSchema || !plan?.coverages) return;
    const uniformCapitalReais =
      parseCurrencyInput(plan.uniformCapital ?? '') ?? undefined;
    const normalized = normalizeHydratedCoverageSelections(
      coverageSchema,
      plan.coverages,
      { uniformCapitalReais }
    );
    if (normalized !== plan.coverages) {
      setPlan({ ...plan, coverages: normalized });
    }
  }, [coverageSchema, plan, setPlan]);

  const [addSubOpen, setAddSubOpen] = useState(false);
  const [addMemberTarget, setAddMemberTarget] = useState<string | null>(null);
  const [memberCpfOverrides, setMemberCpfOverrides] = useState<
    Record<string, string>
  >({});
  // Edição de vida: guarda o alvo (estipulante/sub) + a vida sendo editada.
  const [editMember, setEditMember] = useState<{
    targetId: string;
    member: GroupLifeMember;
  } | null>(null);
  const [importTarget, setImportTarget] = useState<string | null>(null);
  const [duplicatesPayload, setDuplicatesPayload] =
    useState<GroupLifeDuplicateCpfsPayload | null>(null);
  const [addingSubStipulator, setAddingSubStipulator] = useState(false);
  const [importing, setImporting] = useState(false);
  // Overlay central bloqueante para operações CRUD de vida (add/edit/remove),
  // que batem no InsureMO de forma síncrona. Sem ele, o corretor fica sem
  // feedback durante o round-trip e tem a sensação de que o sistema travou.
  // `null` = ocioso; string = mensagem exibida.
  const [crudBusy, setCrudBusy] = useState<string | null>(null);
  // Submissão do modal de vida (add/edit): mantém o modal aberto enquanto a
  // API roda e só fecha no sucesso — um erro NÃO descarta os dados digitados.
  const [memberSubmitting, setMemberSubmitting] = useState(false);

  // Cliente React Query para invalidar as páginas de vidas (server-side) após
  // CRUD/upload, fazendo cada card refetchar e refletir o estado do InsureMO.
  const queryClient = useQueryClient();
  const invalidateBucket = (targetId: string) => {
    if (!policyId) return;
    queryClient.invalidateQueries({
      queryKey: membersPageBucketKey(policyId, targetId),
    });
  };

  const isModalCpfRegistered = useCallback(
    (cpf: string) => {
      if (isCpfRegistered(cpf)) return true;

      const normalizedCpf = (cpf ?? '').replace(/[^\d*]/g, '');
      if (!normalizedCpf || !policyId) return false;

      return queryClient
        .getQueriesData<GroupLifeMembersPageResponse>({
          queryKey: ['group-life', 'members-page', policyId],
        })
        .some(([, page]) =>
          page?.members.some(member => {
            const memberCpf = (member.cpf ?? '').replace(/[^\d*]/g, '');
            if (!memberCpf || memberCpf.length !== normalizedCpf.length) {
              return false;
            }
            return [...normalizedCpf].every(
              (digit, index) =>
                digit === '*' ||
                memberCpf[index] === '*' ||
                memberCpf[index] === digit
            );
          })
        );
    },
    [isCpfRegistered, policyId, queryClient]
  );

  // Totais por bucket reportados por cada MemberGroupCard (contagem real do
  // InsureMO). Fonte do `totalMembers` (gate "Próximo") em vez dos arrays do
  // contexto, que não comportam mais o conjunto completo (5000+ vidas).
  const [bucketTotals, setBucketTotals] = useState<Record<string, number>>({});
  const handleBucketTotalChange = (targetId: string, total: number) => {
    setBucketTotals(prev =>
      prev[targetId] === total ? prev : { ...prev, [targetId]: total }
    );
  };

  // Estado do upload ASSÍNCRONO (Upload Framework do InsureMO). Persistido no
  // localStorage para sobreviver a reload — o batch continua processando
  // server-side mesmo se o corretor recarregar a página.
  const [asyncImport, setAsyncImport] = useState<{
    importId: string;
    targetId: string;
    totalExpected: number;
  } | null>(null);
  const asyncImportLsKey = useMemo(
    () => (policyId ? `group-life-async-import:${policyId}` : null),
    [policyId]
  );
  // Recovery on-mount: restaura job em andamento se houver entry no localStorage.
  useEffect(() => {
    if (!asyncImportLsKey) return;
    try {
      const raw = localStorage.getItem(asyncImportLsKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as typeof asyncImport;
      if (saved?.importId) setAsyncImport(saved);
    } catch {
      /* ignora corrupção do localStorage */
    }
  }, [asyncImportLsKey]);
  const asyncImportQuery = useAsyncMembersImport({
    policyId,
    importId: asyncImport?.importId ?? null,
    enabled: !!asyncImport,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const { save, saving } = useSaveGroupLifeStep();
  const {
    mutateAsync: calculateQuote,
    isPending: calculating,
    error: calcError,
  } = useCalculateGroupLifeQuote();
  const importInFlight =
    (!!asyncImport &&
      (asyncImport.importId === '' ||
        (asyncImportQuery.status !== 'COMPLETED' &&
          asyncImportQuery.status !== 'FAILED'))) ||
    importing ||
    crudBusy != null;
  // Importando/exportando (ou gravando uma vida), nenhuma ação da tela pode
  // começar outra operação sobre as vidas: a importação substitui as vidas do
  // alvo no back e um segundo job sobrescreveria o acompanhamento do primeiro.
  const actionsLocked = isViewMode || importInFlight;

  // TEMPORÁRIO: quando o envio das vidas ao InsureMO falha na importação, as
  // vidas ficam só no contexto local. Sinalizamos a falha para oferecer um
  // botão de "Reenviar vidas ao InsureMO" (reaproveita o save('risk'), cujo
  // diff trata vidas ausentes no store como novas → recria no InsureMO).
  const [syncFailed, setSyncFailed] = useState(false);
  const [syncErrorMsg, setSyncErrorMsg] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Contagem total de vidas = soma dos totais reportados por cada bucket
  // (vindos do InsureMO via MemberGroupCard). Não usa mais os arrays do
  // contexto, que deixaram de comportar o conjunto completo (5000+ vidas).
  // Fallback p/ os comprimentos do contexto enquanto os cards ainda não
  // reportaram (primeiro render), para o gate "Próximo" não bloquear à toa.
  const totalMembers = useMemo(() => {
    const reported = Object.values(bucketTotals);
    if (reported.length > 0) {
      return reported.reduce((acc, n) => acc + n, 0);
    }
    return (
      stipulatorMembers.length +
      subStipulators.reduce((acc, s) => acc + s.members.length, 0)
    );
  }, [bucketTotals, stipulatorMembers, subStipulators]);
  const multiplyRawSalary = plan?.modality === 'MULTIPLE_SALARY';

  // Monta o payload do save('risk') a partir do estado atual do contexto. O
  // back reconcilia por diff (add/editar/remover) contra o store durável.
  const buildRiskPayload = () => {
    const allMembers = [
      ...stipulatorMembers,
      ...subStipulators.flatMap(s => s.members),
    ].map(m => ({
      id: m.id, // identidade estável — chave do diff incremental no back
      cpf: m.cpf,
      name: m.name,
      dateOfBirth: m.dateOfBirth,
      gender: m.gender,
      insurableGroup: m.insurableGroup,
      capital: m.capital,
      awayFromWork: m.awayFromWork,
      cids: m.cids,
    }));
    return {
      subStipulatorsCount: subStipulators.length,
      stipulatorMembersCount: stipulatorMembers.length,
      totalMembers,
      members: allMembers,
    };
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (totalMembers === 0 || saving || calculating || importInFlight) return;
    setSyncFailed(false);
    setSyncErrorMsg(null);
    try {
      await calculateQuote();
    } catch {
      return;
    }
    onNext();
  };

  // TEMPORÁRIO: reenvia as vidas ao InsureMO sem avançar de step. Usa o mesmo
  // save('risk'); o diff do back recria no InsureMO as vidas que não estão no
  // store (as que falharam na importação).
  const handleRetrySendMembers = async () => {
    if (totalMembers === 0 || retrying || saving) return;
    setRetrying(true);
    try {
      const ok = await save('risk', buildRiskPayload());
      if (ok) {
        setSyncFailed(false);
        setSyncErrorMsg(null);
        toaster.create({
          type: 'success',
          title: 'Reenvio de vidas',
          description: 'Vidas reenviadas ao InsureMO com sucesso.',
        });
      } else {
        toaster.create({
          type: 'error',
          title: 'Reenvio de vidas',
          description:
            'Não foi possível reenviar as vidas ao InsureMO. Tente novamente em instantes.',
        });
      }
    } finally {
      setRetrying(false);
    }
  };

  /** Add via modal — chama o back imediatamente, recebe a vida materializada
   *  (com PolicyElementId como id) e popula o grid. Se falhar, mostra alerta
   *  e o modal já está fechado (o corretor pode tentar de novo abrindo). */
  const handleAddMemberConfirm = async (data: Omit<GroupLifeMember, 'id'>) => {
    if (!policyId || !addMemberTarget || memberSubmitting) return;
    const targetId = addMemberTarget;
    const uniqueId = crypto.randomUUID();
    setMemberSubmitting(true);
    try {
      const result = await groupLifeService.addMember({
        quoteId: policyId,
        input: {
          targetId,
          UniqueID: uniqueId,
          cpf: data.cpf,
          name: data.name,
          dateOfBirth: data.dateOfBirth,
          gender: data.gender,
          insurableGroup: data.insurableGroup,
          capital: data.capital,
          awayFromWork: data.awayFromWork,
          cids: data.cids,
        },
      });
      if (!result.success || !result.data) {
        // Mantém o modal ABERTO com os dados — o corretor corrige e tenta de novo.
        toaster.create({
          type: 'error',
          title: 'Adicionar vida',
          description: result.duplicateCpf
            ? 'CPF já cadastrado nesta cotação.'
            : (result.error ?? 'Não foi possível adicionar a vida.'),
        });
        return;
      }
      const m = result.data;
      if (m.id) memberUniqueIdsRef.current.set(m.id, uniqueId);
      rememberMemberCpf(policyId, {
        name: m.name,
        dateOfBirth: m.dateOfBirth,
        cpf: data.cpf,
      });
      const ctxMember = {
        id: m.id,
        cpf: m.cpf,
        name: m.name,
        dateOfBirth: m.dateOfBirth,
        gender: (m.gender || 'M') as 'M' | 'F',
        insurableGroup: m.insurableGroup,
        capital: m.capital,
        awayFromWork: m.awayFromWork,
        cids: m.cids,
      };
      if (targetId === STIPULATOR_TARGET) {
        bulkAddMembersToStipulator([ctxMember]);
      } else {
        bulkAddMembersToSubStipulator(targetId, [ctxMember]);
      }
      // Refetcha a página do bucket (server-side é a fonte do grid agora).
      invalidateBucket(targetId);
      // Sucesso → fecha o modal (limpa no próximo open).
      setAddMemberTarget(null);
    } finally {
      setMemberSubmitting(false);
    }
  };

  /** Edit via modal — chama o back imediatamente e substitui a vida no grid
   *  pela versão materializada devolvida. */
  const handleEditMemberConfirm = async (data: Omit<GroupLifeMember, 'id'>) => {
    if (!policyId || !editMember || memberSubmitting) return;
    const editTarget = editMember;
    const uniqueId =
      memberUniqueIdsRef.current.get(editTarget.member.id) ??
      crypto.randomUUID();
    memberUniqueIdsRef.current.set(editTarget.member.id, uniqueId);
    setMemberSubmitting(true);
    try {
      const result = await groupLifeService.updateMember({
        quoteId: policyId,
        memberId: editTarget.member.id,
        input: {
          UniqueID: uniqueId,
          cpf: data.cpf || null,
          name: data.name,
          dateOfBirth: data.dateOfBirth,
          gender: data.gender,
          insurableGroup: data.insurableGroup,
          capital: data.capital,
          awayFromWork: data.awayFromWork,
          cids: data.cids,
        },
      });
      if (!result.success || !result.data) {
        // Mantém o modal ABERTO com os dados — o corretor corrige e tenta de novo.
        toaster.create({
          type: 'error',
          title: 'Editar vida',
          description: result.error ?? 'Não foi possível atualizar a vida.',
        });
        return;
      }
      const m = result.data;
      setMemberCpfOverrides(current => ({
        ...current,
        [editTarget.member.id]: data.cpf,
      }));
      rememberMemberCpf(policyId, {
        name: m.name,
        dateOfBirth: m.dateOfBirth,
        cpf: data.cpf,
      });
      const ctxData = {
        cpf: data.cpf,
        name: m.name,
        dateOfBirth: m.dateOfBirth,
        gender: (m.gender || 'M') as 'M' | 'F',
        insurableGroup: m.insurableGroup,
        capital: m.capital,
        awayFromWork: m.awayFromWork,
        cids: m.cids,
      };
      if (editTarget.targetId === STIPULATOR_TARGET) {
        updateMemberInStipulator(editTarget.member.id, ctxData);
      } else {
        updateMemberInSubStipulator(
          editTarget.targetId,
          editTarget.member.id,
          ctxData
        );
      }
      invalidateBucket(editTarget.targetId);
      // Sucesso → fecha o modal de edição.
      setEditMember(null);
    } finally {
      setMemberSubmitting(false);
    }
  };

  /** Remove via kebab menu da vida — chama o back imediatamente e remove do
   *  grid se a deleção for confirmada. */
  const handleRemoveMember = async (memberId: string, targetId: string) => {
    if (!policyId) return;
    setCrudBusy('Removendo a vida da cotação…');
    try {
      const result = await groupLifeService.deleteMember({
        quoteId: policyId,
        memberId,
      });
      if (!result.success) {
        toaster.create({
          type: 'error',
          title: 'Remover vida',
          description: result.error ?? 'Não foi possível remover a vida.',
        });
        return;
      }
      if (targetId === STIPULATOR_TARGET) {
        removeMemberFromStipulator(memberId);
      } else {
        removeMemberFromSubStipulator(targetId, memberId);
      }
      invalidateBucket(targetId);
    } finally {
      setCrudBusy(null);
    }
  };

  /**
   * Add subestipulante via modal — persiste no InsureMO via
   * `POST /sub-stipulators` antes de adicionar ao contexto local. Usa o `id`
   * estável devolvido pelo back (RiskGroupId/StipulantRef) — sem isso, vidas
   * roteadas para o sub depois do reload perderiam o vínculo.
   */
  const handleAddSubStipulator = async (data: {
    cnpj: string;
    legalName: string;
    cnaeCode?: string;
    isCaepf: boolean;
    cpf?: string;
    /** Contato opcional — exibido no resumo da cotação. */
    email?: string;
    phone?: string;
  }) => {
    if (!policyId || addingSubStipulator) return;
    setAddingSubStipulator(true);
    try {
      const result = await groupLifeService.addSubStipulator({
        quoteId: policyId,
        input: data,
      });
      if (!result.success || !result.data) {
        toaster.create({
          type: 'error',
          title: 'Adicionar estipulação',
          description:
            result.error ?? 'Não foi possível adicionar a estipulação.',
        });
        return;
      }
      const sub = result.data;
      addSubStipulator({
        id: sub.id,
        cnpj: sub.cnpj,
        legalName: sub.legalName,
        cnaeCode: sub.cnaeCode,
        // O back ecoa o contato do input; o `?? data` cobre snapshot recém-
        // gravado que ainda não devolveu os campos.
        email: sub.email ?? data.email,
        phone: sub.phone ?? data.phone,
        // Identidade CAEPF vem do INPUT (a resposta do back não devolve): sem
        // isto o sub CAEPF perde o tipo/cpf no reload e a proposta o trata como CNPJ.
        isCaepf: data.isCaepf,
        cpf: data.cpf,
      });
      // Só fecha o modal após o sub aparecer no contexto.
      setAddSubOpen(false);
    } finally {
      setAddingSubStipulator(false);
    }
  };

  /**
   * Remove subestipulante — persiste no InsureMO via `DELETE /sub-stipulators/:id`
   * (cascade-delete de vidas atreladas no back) e só então remove do contexto.
   * O back retorna `removedMembersCount` para feedback ao corretor quando há
   * vidas atreladas.
   */
  const handleRemoveSubStipulator = async (subId: string) => {
    if (!policyId) return;
    const result = await groupLifeService.deleteSubStipulator({
      quoteId: policyId,
      subId,
    });
    if (!result.success) {
      toaster.create({
        type: 'error',
        title: 'Remover estipulação',
        description: result.error ?? 'Não foi possível remover a estipulação.',
      });
      return;
    }
    if (result.data && result.data.removedMembersCount > 0) {
      // Feedback informativo — não bloqueia o fluxo.
      console.info(
        `Subestipulante removido com ${result.data.removedMembersCount} vida(s) em cascata.`
      );
    }
    removeSubStipulator(subId);
  };

  const handleImportConfirm = async (file: File) => {
    if (!importTarget || importing) return;
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Importação de vidas',
        description:
          'Não foi possível identificar a cotação atual. Recarregue a página e tente novamente.',
      });
      return;
    }
    // Bifurcação por feature flag: async é o caminho default (Upload Framework
    // do InsureMO); síncrono fica como fallback de DEV.
    if (GROUP_LIFE_USE_ASYNC_IMPORT) {
      return handleImportConfirmAsync(file, importTarget);
    }
    // Captura o alvo e FECHA o modal antes de iniciar o upload, para que o
    // overlay de loading central (controlado por `importing`) fique visível —
    // senão o modal de importação ficaria por cima cobrindo o overlay.
    const targetId = importTarget;
    setImportTarget(null);
    setImporting(true);
    try {
      const result = await groupLifeService.uploadMembersSheet({
        quoteId: policyId,
        targetId,
        file,
        UniqueID: crypto.randomUUID(),
      });
      if (!result.success || !result.data) {
        toaster.create({
          type: 'error',
          title: 'Importação de vidas',
          description:
            result.error ??
            'Não foi possível enviar a planilha. Tente novamente.',
        });
        return;
      }

      const { status, members, errors, totalRowsRead, insuremoSync } =
        result.data;

      if (status === 'PARSE_FAILED') {
        toaster.create({
          type: 'error',
          title: 'Falha ao ler o arquivo Excel',
          description: errors.map(e => e.message).join(' · '),
        });
        return;
      }

      // Mapeia ParsedMember (back) → GroupLifeMember (contexto front).
      // Preserva o `id` atribuído no back (import) — é a âncora do diff
      // incremental do Step 3. Vidas sem id ganham um no contexto.
      const mapped = members
        .filter(m => !!m.dateOfBirth)
        .map(m => ({
          id: m.id,
          cpf: m.cpf,
          name: m.name,
          dateOfBirth: m.dateOfBirth,
          gender: (m.gender || 'M') as 'M' | 'F',
          insurableGroup: m.insurableGroup,
          capital: m.capital,
          awayFromWork: m.awayFromWork,
          cids: m.cids,
        }));

      // Dedup de CPF: ignora vidas cujo CPF já existe na cotação
      // (estipulante/subestipulantes) E vidas com CPF repetido dentro do
      // próprio arquivo. CPFs vazios (vidas não-nomeadas) nunca duplicam.
      const seenInBatch = new Set<string>();
      const toContextMembers: typeof mapped = [];
      let skippedDuplicates = 0;
      for (const m of mapped) {
        const clean = (m.cpf ?? '').replace(/\D/g, '');
        if (clean) {
          if (isCpfRegistered(clean) || seenInBatch.has(clean)) {
            skippedDuplicates += 1;
            continue;
          }
          seenInBatch.add(clean);
        }
        toContextMembers.push(m);
      }

      if (targetId === STIPULATOR_TARGET) {
        bulkAddMembersToStipulator(toContextMembers);
      } else {
        bulkAddMembersToSubStipulator(targetId, toContextMembers);
      }
      // Grid server-side: refetcha a página do bucket após o import síncrono.
      invalidateBucket(targetId);

      // Vidas ignoradas por duplicidade: considera o dedup local (contexto) e
      // o do InsureMO (vidas já persistidas) — usa o maior dos dois.
      const totalSkipped = Math.max(
        skippedDuplicates,
        insuremoSync?.skippedDuplicates ?? 0
      );
      const dupSuffix =
        totalSkipped > 0
          ? `\n${totalSkipped} vida(s) ignorada(s) por CPF já cadastrado.`
          : '';

      // TEMPORÁRIO: marca falha de sync para exibir o botão de reenvio.
      if (insuremoSync?.status === 'FAILED') {
        setSyncFailed(true);
        setSyncErrorMsg(insuremoSync.error ?? null);
      }

      // Resultado do envio das vidas ao InsureMO.
      const syncSuffix =
        insuremoSync?.status === 'FAILED'
          ? `\n\n⚠ Falha ao enviar as vidas ao InsureMO: ${insuremoSync.error ?? 'erro desconhecido'}. As vidas foram importadas localmente; tente reenviar.`
          : insuremoSync?.status === 'SENT'
            ? `\n${insuremoSync.sentCount} vida(s) enviada(s) ao InsureMO.`
            : insuremoSync?.status === 'SKIPPED' && totalSkipped > 0
              ? `\nNenhuma vida nova enviada — todas já estavam cadastradas.`
              : '';

      if (status === 'PARSED_WITH_ERRORS') {
        const summary = errors
          .slice(0, 5)
          .map(e => `• linha ${e.rowNumber} (${e.column}): ${e.message}`)
          .join('\n');
        const overflow =
          errors.length > 5 ? `\n…e mais ${errors.length - 5} erros.` : '';
        toaster.create({
          type: 'warning',
          title: 'Planilha processada com avisos',
          description: `${totalRowsRead} linhas lidas · ${toContextMembers.length} vidas importadas · ${errors.length} erros.${dupSuffix}${syncSuffix}\n${summary}${overflow}`,
        });
      } else {
        toaster.create({
          type: 'success',
          title: 'Importação de vidas',
          description: `Planilha processada com sucesso! ${toContextMembers.length} vidas importadas.${dupSuffix}${syncSuffix}`,
        });
      }
    } finally {
      setImporting(false);
    }
  };

  /**
   * Caminho ASSÍNCRONO de importação — NÃO trava a tela em momento nenhum.
   * O widget flutuante aparece IMEDIATAMENTE ao clicar Confirmar (em estado
   * "Enviando ao sistema…"); quando o POST retorna o `importId`, o hook
   * `useAsyncMembersImport` começa a fazer polling de 3 em 3s e o widget
   * passa a mostrar progresso real. O corretor pode continuar navegando,
   * adicionando subestipulantes e vidas manualmente normalmente.
   */
  const handleImportConfirmAsync = async (file: File, targetId: string) => {
    if (!policyId) return;
    setImportTarget(null);
    // Widget aparece já como "Enviando ao sistema…" enquanto o POST roda
    // (importId vazio mantém o hook desabilitado até termos o id real).
    setAsyncImport({ importId: '', targetId, totalExpected: 0 });

    const result = await groupLifeService.uploadMembersSheetAsync({
      quoteId: policyId,
      targetId,
      file,
      // Modalidade não é persistida no back — enviamos o contexto do plano
      // para aplicar as regras de capital por modalidade na importação.
      plan: {
        modality: plan?.modality,
        uniformCapital: plan?.uniformCapital,
        numberOfSalaries: plan?.numberOfSalaries,
        minimumIndividualCapital: plan?.minimumIndividualCapital,
        maximumIndividualCapital: plan?.maximumIndividualCapital,
      },
    });
    if (!result.success || !result.data) {
      setAsyncImport(null);
      // CPFs duplicados (status 422 com payload estruturado): abre o modal
      // dedicado com a lista agrupada (já cadastrados × repetidos na planilha)
      // em vez de um alert cru.
      if (result.duplicates) {
        setDuplicatesPayload(result.duplicates);
        return;
      }
      toaster.create({
        type: 'error',
        title: 'Importação de vidas',
        description:
          result.error ??
          'Não foi possível enviar a planilha. Tente novamente.',
      });
      return;
    }
    const { importId, totalExpected } = result.data;

    // Preview PROGRESSIVO: o grid é server-side; em vez de semear o contexto,
    // o effect de polling abaixo invalida a página do bucket conforme o
    // InsureMO materializa as vidas — elas vão aparecendo parcialmente no
    // grid até 100%.

    // Persiste no localStorage para resumir polling após reload.
    const stateToPersist = { importId, targetId, totalExpected };
    setAsyncImport(stateToPersist);
    if (asyncImportLsKey) {
      try {
        localStorage.setItem(asyncImportLsKey, JSON.stringify(stateToPersist));
      } catch {
        /* quota cheia — segue sem persistência */
      }
    }
  };

  // Preview PROGRESSIVO + COMPLETED/FAILED do upload async. O grid é
  // server-side: em vez de semear o contexto, invalidamos a página do bucket
  // conforme o InsureMO materializa as vidas — elas aparecem crescendo no
  // grid até 100%. SEM alert() — o widget flutuante já dá o feedback.
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInvalidatedCountRef = useRef<number>(-1);

  // Refetch progressivo THROTTLED: invalida a página do bucket alvo só a cada
  // 25 vidas de avanço no `totalCreated` — invalidar a cada poll de 3s
  // recarregava o grid server-side continuamente e pesava a importação toda.
  // O refetch do estado final fica garantido pelo invalidate do COMPLETED.
  const ASYNC_IMPORT_INVALIDATE_EVERY = 25;
  const asyncCreated = asyncImportQuery.progress.current;
  useEffect(() => {
    if (!asyncImport || !asyncImport.importId || !policyId) return;
    if (
      asyncCreated - Math.max(0, lastInvalidatedCountRef.current) <
      ASYNC_IMPORT_INVALIDATE_EVERY
    )
      return;
    lastInvalidatedCountRef.current = asyncCreated;
    queryClient.invalidateQueries({
      queryKey: membersPageBucketKey(policyId, asyncImport.targetId),
    });
  }, [asyncCreated, asyncImport?.importId, policyId]);

  useEffect(() => {
    if (!asyncImport || !asyncImport.importId) {
      lastInvalidatedCountRef.current = -1;
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      return;
    }
    if (asyncImportQuery.status === 'COMPLETED') {
      // Garante o último refetch (estado final) e agenda auto-dismiss do widget.
      if (policyId) {
        queryClient.invalidateQueries({
          queryKey: membersPageBucketKey(policyId, asyncImport.targetId),
        });
      }
      if (asyncImportLsKey) localStorage.removeItem(asyncImportLsKey);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => {
        setAsyncImport(null);
        dismissTimerRef.current = null;
      }, 4000);
      return;
    }
    if (asyncImportQuery.status === 'FAILED') {
      if (asyncImportLsKey) localStorage.removeItem(asyncImportLsKey);
    }
  }, [asyncImport, asyncImportQuery.status, asyncImportLsKey, policyId]);

  // Cleanup do timer de auto-dismiss no unmount do componente.
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  /** Fecha o widget flutuante (botão X). Limpa também o localStorage. */
  const handleDismissAsyncWidget = () => {
    if (asyncImportLsKey) localStorage.removeItem(asyncImportLsKey);
    setAsyncImport(null);
  };

  /** Exporta as vidas de um bucket (estipulante master ou sub) em .xlsx — o
   *  back gera o arquivo (CPF completo do store) e devolve uma URL de
   *  download. Reusa o overlay `crudBusy` para feedback (pode levar alguns
   *  segundos com milhares de vidas). */
  const handleExport = async (targetId: string, label: string) => {
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Exportar vidas',
        description: 'Não foi possível identificar a cotação atual.',
      });
      return;
    }
    setCrudBusy('Gerando a planilha de vidas…');
    try {
      const result = await groupLifeService.downloadMembersExport({
        quoteId: policyId,
        targetId,
        filename: label,
      });
      if (!result.success) {
        toaster.create({
          type: 'error',
          title: 'Exportar vidas',
          description: result.error ?? 'Não foi possível exportar as vidas.',
        });
        return;
      }
      if (result.total === 0) {
        toaster.create({
          type: 'info',
          title: 'Exportar vidas',
          description: 'Não há vidas cadastradas neste grupo para exportar.',
        });
      }
    } finally {
      setCrudBusy(null);
    }
  };

  const showEmptyError = submitAttempted && totalMembers === 0;

  const stipulatorTitle = stipulator?.responsibleName || 'Estipulante';
  const stipulatorDoc = stipulator?.registerNumber || '';
  const stipulatorCnae = stipulator?.cnaeCode ?? '';

  return (
    <div className='space-y-6'>
      {/* Overlay de carregamento centralizado durante a importação da planilha.
          Bloqueia a tela e deixa claro que o sistema está processando (evita
          a impressão de "travou" enquanto o upload/parse/envio ao InsureMO
          acontece). */}
      {importing && !asyncImport && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'
          role='status'
          aria-live='polite'
          aria-busy='true'
        >
          <div className='flex flex-col items-center gap-4 rounded-2xl bg-white px-10 py-8 shadow-xl'>
            <Loader2
              className='h-10 w-10 animate-spin text-alba-green-500'
              strokeWidth={1.8}
            />
            <div className='text-center'>
              <Text className='text-base font-bold text-alba-black-500'>
                Importando as vidas da sua cotação…
              </Text>
              <Text className='mt-1 text-sm text-alba-gray-600'>
                Estamos preparando as vidas da sua planilha. Em instantes elas
                estarão prontas na cotação — por favor, não feche esta tela.
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Overlay central bloqueante das operações CRUD de vida (add/edit/remove).
          Persiste enquanto o round-trip síncrono ao InsureMO acontece — sem
          ele o corretor não tem feedback e acha que o sistema travou. */}
      {crudBusy && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'
          role='status'
          aria-live='polite'
          aria-busy='true'
        >
          <div className='flex flex-col items-center gap-4 rounded-2xl bg-white px-10 py-8 shadow-xl'>
            <Loader2
              className='h-10 w-10 animate-spin text-alba-green-500'
              strokeWidth={1.8}
            />
            <div className='text-center'>
              <Text className='text-base font-bold text-alba-black-500'>
                {crudBusy}
              </Text>
              <Text className='mt-1 text-sm text-alba-gray-600'>
                Estamos sincronizando com o sistema. Por favor, não feche esta
                tela.
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Widget FLUTUANTE não-bloqueante do upload ASSÍNCRONO. Fica no canto
          inferior direito enquanto o batch do InsureMO processa em background.
          O corretor pode continuar trabalhando normalmente (adicionar
          subestipulantes, vidas manualmente, navegar entre steps).
          Sobrevive a reload via localStorage. */}
      {asyncImport && (
        <AsyncImportOverlay
          status={asyncImportQuery.status}
          progress={asyncImportQuery.progress}
          errors={asyncImportQuery.errors}
          onDismiss={handleDismissAsyncWidget}
        />
      )}

      {/* DEV-only: limpa todas as vidas para facilitar testes locais. Só
          renderiza em ambiente de desenvolvimento (import.meta.env.DEV). */}
      {import.meta.env.DEV && totalMembers > 0 && !isViewMode && (
        <div className='flex items-center justify-between rounded-md border border-dashed border-alba-error-red-300 bg-alba-error-red-50 p-3'>
          <Text className='text-xs text-alba-error-red-700'>
            DEV — ferramenta de teste local
          </Text>
          <Button
            variant='ghost'
            size='small'
            disabled={importInFlight}
            onClick={() => {
              if (
                window.confirm(
                  `Remover todas as ${totalMembers} vida(s) da cotação? (apenas local)`
                )
              ) {
                clearAllMembers();
              }
            }}
          >
            <Trash2 className='w-4 h-4' strokeWidth={1.8} />
            Remover todas as vidas
          </Button>
        </div>
      )}

      {showEmptyError && (
        <div className='rounded-md border border-alba-error-red-200 bg-alba-error-red-50 p-3 text-alba-error-red-700'>
          <Text className='text-sm'>
            Inclua ao menos 1 vida (no Estipulante ou em um Subestipulante) para
            prosseguir.
          </Text>
        </div>
      )}

      {/* TEMPORÁRIO (DEV-only): botão de reenvio quando a importação não
          conseguiu enviar as vidas ao InsureMO. Reaproveita o save('risk').
          Só renderiza em ambiente local (import.meta.env.DEV). */}
      {import.meta.env.DEV && syncFailed && totalMembers > 0 && !isViewMode && (
        <div className='flex items-center justify-between gap-4 rounded-md border border-alba-error-red-300 bg-alba-error-red-50 p-3'>
          <Text className='text-sm text-alba-error-red-700'>
            Algumas vidas não foram enviadas ao InsureMO
            {syncErrorMsg ? `: ${syncErrorMsg}` : '.'} As vidas estão salvas
            localmente. Você pode tentar reenviá-las.
          </Text>
          <Button
            variant='secondary'
            size='small'
            onClick={handleRetrySendMembers}
            disabled={retrying || saving || importInFlight}
          >
            {retrying ? 'Reenviando…' : 'Reenviar vidas ao InsureMO'}
          </Button>
        </div>
      )}

      {calcError ? (
        <div className='rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {calcError instanceof Error
            ? calcError.message
            : 'Erro ao calcular a cotação'}
        </div>
      ) : null}

      <MemberGroupCard
        cardTitle='Estipulante'
        cardIcon={<Building className='w-5 h-5 text-alba-violet-500' />}
        legalName={stipulatorTitle}
        cnaeCode={stipulatorCnae}
        documentLabel={stipulator?.isCaepf ? 'CAEPF' : 'CNPJ'}
        documentValue={stipulatorDoc}
        policyId={policyId}
        targetId={STIPULATOR_TARGET}
        contextMembers={stipulatorMembers}
        memberCpfOverrides={memberCpfOverrides}
        planModality={plan?.modality}
        uniformCapital={plan?.uniformCapital}
        numberOfSalaries={plan?.numberOfSalaries}
        minimumIndividualCapital={plan?.minimumIndividualCapital}
        maximumIndividualCapital={plan?.maximumIndividualCapital}
        multiplySalary={multiplyRawSalary}
        onTotalChange={handleBucketTotalChange}
        showAddSubStipulator
        onAddSubStipulator={() => setAddSubOpen(true)}
        onExport={() => handleExport(STIPULATOR_TARGET, stipulatorTitle)}
        onImport={() => setImportTarget(STIPULATOR_TARGET)}
        onAddMember={() => setAddMemberTarget(STIPULATOR_TARGET)}
        onRemoveMember={id => handleRemoveMember(id, STIPULATOR_TARGET)}
        onEditMember={member =>
          setEditMember({ targetId: STIPULATOR_TARGET, member })
        }
        disabled={actionsLocked}
      />

      {subStipulators.map((sub, idx) => (
        <MemberGroupCard
          key={sub.id}
          cardTitle={`Subestipulante ${idx + 1}`}
          cardIcon={<Building2 className='w-5 h-5 text-alba-black-500' />}
          legalName={sub.legalName}
          cnaeCode={sub.cnaeCode ?? ''}
          documentLabel='CNPJ'
          documentValue={sub.cnpj}
          policyId={policyId}
          targetId={sub.id}
          contextMembers={sub.members}
          memberCpfOverrides={memberCpfOverrides}
          planModality={plan?.modality}
          uniformCapital={plan?.uniformCapital}
          numberOfSalaries={plan?.numberOfSalaries}
          minimumIndividualCapital={plan?.minimumIndividualCapital}
          maximumIndividualCapital={plan?.maximumIndividualCapital}
          multiplySalary={multiplyRawSalary}
          onTotalChange={handleBucketTotalChange}
          onRemove={() => handleRemoveSubStipulator(sub.id)}
          onExport={() => handleExport(sub.id, sub.legalName)}
          onImport={() => setImportTarget(sub.id)}
          onAddMember={() => setAddMemberTarget(sub.id)}
          onRemoveMember={id => handleRemoveMember(id, sub.id)}
          onEditMember={member => setEditMember({ targetId: sub.id, member })}
          disabled={actionsLocked}
        />
      ))}

      <div className='flex justify-between border-t border-alba-gray-200 pt-6'>
        <Button
          variant='ghost'
          size='large'
          onClick={onBack}
          disabled={calculating || importInFlight}
        >
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <div className='flex items-center gap-3'>
          {importInFlight && !isViewMode ? (
            <Text className='text-sm text-alba-gray-500'>
              Aguarde a importação de vidas concluir
            </Text>
          ) : null}
          <Button
            variant='primary'
            size='large'
            onClick={handleSubmit}
            disabled={isViewMode || calculating || importInFlight}
          >
            {calculating ? 'Calculando…' : 'Calcular'}
            {calculating ? (
              <Loader2 className='w-5 h-5 animate-spin' strokeWidth={1.6} />
            ) : (
              <Calculator className='w-5 h-5' strokeWidth={1.6} />
            )}
          </Button>
        </div>
      </div>

      <AddSubStipulatorModal
        isOpen={addSubOpen}
        onClose={() => {
          if (!addingSubStipulator) setAddSubOpen(false);
        }}
        onConfirm={data => handleAddSubStipulator(data)}
        stipulatorDocument={stipulator?.registerNumber}
        existingSubStipulatorDocuments={subStipulators.map(s => s.cnpj)}
        isSubmitting={addingSubStipulator}
      />

      {/* Vidas sem CPF podem ser cadastradas. Quando informado, bloqueamos
          duplicatas já carregadas na cotação antes do envio ao back. */}
      <AddMemberModal
        isOpen={addMemberTarget !== null}
        onClose={() => {
          if (!memberSubmitting) setAddMemberTarget(null);
        }}
        onConfirm={handleAddMemberConfirm}
        isCpfRegistered={isModalCpfRegistered}
        modality={plan?.modality}
        uniformCapital={plan?.uniformCapital}
        minIndividualCapital={plan?.minimumIndividualCapital}
        maxIndividualCapital={plan?.maximumIndividualCapital}
        numberOfSalaries={plan?.numberOfSalaries}
        isSubmitting={memberSubmitting}
      />

      {/* Modal de EDIÇÃO de vida — reusa o AddMemberModal com dados iniciais. */}
      <AddMemberModal
        isOpen={editMember !== null}
        onClose={() => {
          if (!memberSubmitting) setEditMember(null);
        }}
        onConfirm={handleEditMemberConfirm}
        isCpfRegistered={() => false}
        modality={plan?.modality}
        uniformCapital={plan?.uniformCapital}
        minIndividualCapital={plan?.minimumIndividualCapital}
        maxIndividualCapital={plan?.maximumIndividualCapital}
        numberOfSalaries={plan?.numberOfSalaries}
        isSubmitting={memberSubmitting}
        initialMember={
          editMember
            ? {
                cpf: editMember.member.cpf,
                name: editMember.member.name,
                dateOfBirth: editMember.member.dateOfBirth,
                gender: editMember.member.gender,
                insurableGroup: editMember.member.insurableGroup,
                capital: editMember.member.capital,
                awayFromWork: editMember.member.awayFromWork,
                cids: editMember.member.cids,
              }
            : undefined
        }
      />

      <ImportMembersModal
        isOpen={importTarget !== null}
        onClose={() => setImportTarget(null)}
        onConfirm={handleImportConfirm}
        targetName={(() => {
          if (importTarget === STIPULATOR_TARGET)
            return stipulator?.responsibleName?.trim() || 'Estipulante';
          const idx = subStipulators.findIndex(s => s.id === importTarget);
          const sub = idx >= 0 ? subStipulators[idx] : undefined;
          return sub?.legalName?.trim() || `Subestipulante ${idx + 1}`;
        })()}
        replaceNotice={(() => {
          if (importTarget === null) return undefined;
          const isStipulator = importTarget === STIPULATOR_TARGET;
          // Total do servidor; antes da 1ª página carregar, a lista local.
          const existing =
            bucketTotals[importTarget] ??
            (isStipulator
              ? stipulatorMembers.length
              : (subStipulators.find(s => s.id === importTarget)?.members
                  .length ?? 0));
          if (existing === 0) return undefined;
          return `As vidas já cadastradas neste ${
            isStipulator ? 'Estipulante' : 'Subestipulante'
          } serão substituídas pelas da planilha.`;
        })()}
      />

      <DuplicateMembersDialog
        isOpen={duplicatesPayload !== null}
        onClose={() => setDuplicatesPayload(null)}
        payload={duplicatesPayload}
      />
    </div>
  );
}
//-----------------------------------------------------------------------------


type MemberGroupCardProps = {
  cardTitle: string;
  cardIcon: React.ReactNode;
  legalName: string;
  cnaeCode: string;
  documentLabel: string;
  documentValue: string;
  /** Cotação atual — chave da paginação server-side. */
  policyId: string | null;
  /** Target do bucket: `__STIPULATOR__` (master) ou id do subestipulante. */
  targetId: string;
  /**
   * Vidas do contexto local deste bucket (recém-adicionadas/editadas nesta
   * sessão). Usadas para sobrepor campos que o grid server-side pode vir sem
   * preencher (capital/grupo segurável), enquanto o backend enriquece esses
   * campos só do store durável (match que pode falhar/atrasar).
   */
  contextMembers?: GroupLifeMember[];
  /** CPFs alterados nesta sessão, inclusive remoções ainda não refletidas no
   *  resultado paginado do servidor. */
  memberCpfOverrides: Record<string, string>;
  planModality?: string;
  uniformCapital?: string;
  /** Plano em Múltiplo Salarial — usados para exibir o capital efetivo da vida. */
  numberOfSalaries?: string;
  minimumIndividualCapital?: string;
  maximumIndividualCapital?: string;
  /**
   * Múltiplo Salarial: a coluna mostra salário × quantidade de salários.
   * O valor passado à edição continua sendo o salário cru.
   */
  multiplySalary?: boolean;
  /** Reporta ao parent o total real (InsureMO) deste bucket. */
  onTotalChange?: (targetId: string, total: number) => void;
  /** Whether to render the "Adicionar Subestipulante" button (only on Stipulator). */
  showAddSubStipulator?: boolean;
  onAddSubStipulator?: () => void;
  onExport: () => void;
  onImport: () => void;
  onAddMember: () => void;
  /** When defined, renders a "Remover" option in the card-level menu (SubStipulator only). */
  onRemove?: () => void;
  onRemoveMember: (memberId: string) => void;
  onEditMember: (member: GroupLifeMember) => void;
  disabled?: boolean;
};

const PAGE_SIZE_OPTIONS = [
  { value: '6', label: '06' },
  { value: '12', label: '12' },
  { value: '24', label: '24' },
  { value: '48', label: '48' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'OK', label: 'Ativos' },
  { value: 'WARNING', label: 'Pendentes' },
];

function MemberGroupCard({
  cardTitle,
  cardIcon,
  legalName,
  cnaeCode,
  documentLabel,
  documentValue,
  policyId,
  targetId,
  contextMembers,
  memberCpfOverrides,
  planModality,
  uniformCapital,
  numberOfSalaries,
  minimumIndividualCapital,
  maximumIndividualCapital,
  multiplySalary = false,
  onTotalChange,
  showAddSubStipulator,
  onAddSubStipulator,
  onExport,
  onImport,
  onAddMember,
  onRemove,
  onRemoveMember,
  onEditMember,
  disabled,
}: MemberGroupCardProps) {
  const { data: cnaeInfo } = useCnaeLabel(cnaeCode || undefined);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Debounce da busca (350ms) — só o valor debounced entra na query key, pra
  // não refetchar a cada tecla.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(h);
  }, [search]);

  const cnaeDescription = cnaeInfo?.description ?? '';

  const subtitleLine = useMemo(() => {
    const cnae = cnaeCode || '0000000';
    const desc = cnaeDescription || 'ATIVIDADE';
    return `${cnae} - ${desc}`;
  }, [cnaeCode, cnaeDescription]);

  // Paginação SERVER-SIDE. `total` é o real do InsureMO e toda página até ele
  // é alcançável (a Api contorna o teto de 1000 do InsureMO).
  const membersQuery = useMembersPage({
    policyId,
    targetId,
    pageNo: page,
    pageSize,
    search: debouncedSearch,
  });

  // Reporta o total real ao parent (gate "Próximo" + contagem global).
  useEffect(() => {
    onTotalChange?.(targetId, membersQuery.total);
  }, [onTotalChange, targetId, membersQuery.total]);

  // Status filter aplicado apenas sobre a página atual (não-funcional no
  // conjunto total — aceito na fase de testes).
  const pageMembers = useMemo(() => {
    if (statusFilter === 'ALL') return membersQuery.members;
    return membersQuery.members.filter(m => memberStatus(m) === statusFilter);
  }, [membersQuery.members, statusFilter]);

  const ctxMemberLookup = useMemo(() => {
    const byId = new Map<string, GroupLifeMember>();
    const byNameDob = new Map<string, GroupLifeMember>();
    for (const m of contextMembers ?? []) {
      if (m.id) byId.set(m.id, m);
      if (m.name) {
        byNameDob.set(
          `${m.name.trim().toUpperCase()}|${m.dateOfBirth ?? ''}`,
          m
        );
      }
    }
    return { byId, byNameDob };
  }, [contextMembers]);

  const displayMembers = useMemo(() => {
    return pageMembers.map(m => {
      const ctx =
        ctxMemberLookup.byId.get(m.id) ??
        ctxMemberLookup.byNameDob.get(
          `${(m.name ?? '').trim().toUpperCase()}|${m.dateOfBirth ?? ''}`
        );
      const cpfOverride = memberCpfOverrides[m.id];
      const serverCpfUsable = !!m.cpf && !m.cpf.includes('*');
      const cpf =
        cpfOverride ??
        (serverCpfUsable
          ? m.cpf
          : ctx?.cpf ||
            recallMemberCpf(policyId ?? '', {
              name: m.name,
              dateOfBirth: m.dateOfBirth,
            }) ||
            m.cpf);
      if (!ctx) {
        const capital = resolveMemberDisplayCapital(m.capital, {
          modality: planModality,
          uniformCapital,
          numberOfSalaries,
          minimumIndividualCapital,
          maximumIndividualCapital,
        });
        const base = cpf === m.cpf ? m : { ...m, cpf };
        return { ...base, capital };
      }
      return {
        ...m,
        cpf,
        capital: resolveMemberDisplayCapital(m.capital || ctx.capital || '', {
          modality: planModality,
          uniformCapital,
          numberOfSalaries,
          minimumIndividualCapital,
          maximumIndividualCapital,
        }),
        insurableGroup: m.insurableGroup || ctx.insurableGroup || '',
      };
    });
  }, [
    pageMembers,
    ctxMemberLookup,
    memberCpfOverrides,
    policyId,
    planModality,
    uniformCapital,
  ]);

  const total = membersQuery.total;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = pageStart + pageMembers.length;

  // Reset page quando muda busca debounced ou pageSize.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  // Clamp da página se o total encolheu (ex.: após remover a última vida da
  // última página) — evita ficar numa página vazia.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className='rounded-lg border border-alba-gray-200 bg-white'>
      {/* Card header — title + action buttons */}
      <div className='flex flex-wrap items-center justify-between gap-3 p-4 border-b border-alba-gray-200'>
        <div className='flex items-center gap-2'>
          {cardIcon}
          <Text className='text-lg font-semibold text-alba-black-500'>
            {cardTitle}
          </Text>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {showAddSubStipulator && (
            <Button
              variant='ghost'
              size='medium'
              onClick={onAddSubStipulator}
              disabled={disabled}
            >
              <Plus className='w-4 h-4' strokeWidth={1.8} />
              Adicionar Subestipulante
            </Button>
          )}
          <Button
            variant='ghost'
            size='medium'
            onClick={onExport}
            disabled={disabled}
          >
            <Download className='w-4 h-4' strokeWidth={1.8} />
            Exportar
          </Button>
          <Button
            variant='ghost'
            size='medium'
            onClick={onImport}
            disabled={disabled}
          >
            <Upload className='w-4 h-4' strokeWidth={1.8} />
            Importar
          </Button>
          <Button
            variant='secondary'
            size='medium'
            onClick={onAddMember}
            disabled={disabled}
          >
            <UserPlus className='w-4 h-4' strokeWidth={1.8} />
            Adicionar Vida
          </Button>
          {onRemove && (
            <KebabMenu
              items={[
                {
                  label: `Remover ${cardTitle}`,
                  onClick: onRemove,
                  destructive: true,
                },
              ]}
              disabled={disabled}
              ariaLabel={`Ações de ${cardTitle}`}
            />
          )}
        </div>
      </div>

      {/* Razão Social + Atividade */}
      <div className='p-4 pb-2 space-y-0.5'>
        <Text className='text-base font-bold text-alba-black-500'>
          {legalName || 'Razão Social Estipulante'}
        </Text>
        <Text className='text-xs text-alba-gray-500'>{subtitleLine}</Text>
        {documentValue && (
          <Text className='text-xs text-alba-gray-500'>
            {documentLabel}: {documentValue}
          </Text>
        )}
      </div>

      <ul
        aria-label='Legenda dos status das vidas'
        className='flex list-none flex-wrap gap-x-6 gap-y-2 px-4 pb-4 text-sm text-alba-black-500'
      >
        <li className='flex items-center gap-2'>
          <span
            aria-hidden='true'
            className='h-2.5 w-2.5 shrink-0 rounded-full bg-alba-success-green-500'
          />
          Verde: Sem afastamento.
        </li>
        <li className='flex items-center gap-2'>
          <span
            aria-hidden='true'
            className='h-2.5 w-2.5 shrink-0 rounded-full bg-alba-gray-400'
          />
          Cinza: Afastamento.
        </li>
      </ul>

      {/* Filters: Status + Buscar — constrained to the left half of the card
          to mirror the Figma layout. */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 px-4 pb-4 lg:max-w-2xl'>
        <div className='space-y-1'>
          <label className='text-xs font-semibold text-alba-black-500'>
            Status
          </label>
          <Select.Root
            value={[statusFilter]}
            onValueChange={d =>
              setStatusFilter((d.value[0] as StatusFilter) || 'ALL')
            }
            items={STATUS_FILTER_OPTIONS}
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder='Todos' />
                <Select.Indicator />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {STATUS_FILTER_OPTIONS.map(o => (
                  <Select.Item key={o.value} item={o}>
                    <Select.ItemText>{o.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </div>
        <div className='space-y-1'>
          <label className='text-xs font-semibold text-alba-black-500'>
            Buscar
          </label>
          <div className='relative'>
            <input
              type='text'
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Digite'
              className='w-full h-10 rounded-md border border-alba-gray-200 px-3 pr-9 text-sm outline-none placeholder:text-alba-gray-400 focus:border-alba-violet-500 focus:ring-2 focus:ring-alba-violet-100'
            />
            <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-alba-gray-400' />
          </div>
        </div>
      </div>

      {/* Members table */}
      <div className='px-4 pb-4'>
        <div className='overflow-x-auto rounded-2xl border border-alba-gray-200'>
          <table
            className='w-full text-sm'
            style={{ borderCollapse: 'separate', borderSpacing: 0 }}
          >
            <thead>
              <tr className='text-left text-xs uppercase tracking-wider text-[#1A1A1A]'>
                <SortableTh className='w-32 pl-6 py-4 bg-[#EFEFEF]'>
                  Status
                </SortableTh>
                <SortableTh className='py-4 bg-[#EFEFEF]'>
                  Grupo Segurável
                </SortableTh>
                <SortableTh className='py-4 bg-[#EFEFEF]'>Capital</SortableTh>
                <SortableTh className='w-20 py-4 bg-[#EFEFEF]'>Sexo</SortableTh>
                <SortableTh className='py-4 bg-[#EFEFEF]'>
                  Data Nascimento
                </SortableTh>
                <SortableTh className='py-4 bg-[#EFEFEF]'>Nome</SortableTh>
                <th className='py-4 pr-6 w-24 text-right font-bold bg-[#EFEFEF]'>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {pageMembers.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className='py-12 text-center text-alba-gray-500'
                  >
                    {membersQuery.isFetching
                      ? 'Carregando…'
                      : 'Nenhum registro localizado'}
                  </td>
                </tr>
              ) : (
                displayMembers.map(m => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    expanded={!!expanded[m.id]}
                    onToggleExpand={() =>
                      setExpanded(prev => ({ ...prev, [m.id]: !prev[m.id] }))
                    }
                    onRemove={() => onRemoveMember(m.id)}
                    onEdit={() => onEditMember(m)}
                    disabled={disabled}
                    multiplySalary={multiplySalary}
                    plan={{
                      modality: planModality,
                      uniformCapital,
                      numberOfSalaries,
                      minimumIndividualCapital,
                      maximumIndividualCapital,
                    }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className='flex flex-wrap items-center justify-between gap-3 px-4 pb-4'>
          <Pagination
            page={safePage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
          <div className='flex items-center gap-2 text-sm text-alba-gray-700'>
            <div className='w-20'>
              <Select.Root
                value={[String(pageSize)]}
                onValueChange={d => setPageSize(Number(d.value[0] ?? '6'))}
                items={PAGE_SIZE_OPTIONS}
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText />
                    <Select.Indicator />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {PAGE_SIZE_OPTIONS.map(o => (
                      <Select.Item key={o.value} item={o}>
                        <Select.ItemText>{o.label}</Select.ItemText>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </div>
            <Text className='text-sm text-alba-gray-600'>
              Visualizando {pageMembers.length === 0 ? 0 : pageStart + 1} -{' '}
              {pageEnd.toString().padStart(2, '0')} de {total} registros
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MemberRow — single row with expand toggle, status dot, kebab menu and the
// expanded coverages sub-table
// ─────────────────────────────────────────────────────────────────────────────

function MemberRow({
  member,
  expanded,
  onToggleExpand,
  onRemove,
  onEdit,
  disabled,
  multiplySalary,
  plan,
}: {
  member: GroupLifeMember;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onEdit: () => void;
  disabled?: boolean;
  multiplySalary: boolean;
  plan: {
    modality?: string;
    uniformCapital?: string;
    numberOfSalaries?: string;
    minimumIndividualCapital?: string;
    maximumIndividualCapital?: string;
  };
}) {
  const shownCapital = multiplySalary
    ? resolveMemberDisplayCapital(member.capital, plan, {
        multiplySalary: true,
      }) ||
      member.capital ||
      '—'
    : member.capital || '—';

  return (
    <>
      <tr className='border-b border-alba-gray-100'>
        <td className='py-3 pl-6 pr-3 align-middle'>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={onToggleExpand}
              className='p-0.5 rounded hover:bg-alba-gray-100 transition-colors'
              aria-label={expanded ? 'Recolher' : 'Expandir'}
            >
              <ChevronRight
                className={`w-4 h-4 text-alba-gray-600 transition-transform ${expanded ? 'rotate-90' : ''}`}
                strokeWidth={1.8}
              />
            </button>
            <StatusDot awayFromWork={member.awayFromWork} />
          </div>
        </td>
        <td className='py-3 px-3 align-middle'>
          <InsurableGroupLabel value={member.insurableGroup} />
        </td>
        <td className='py-3 px-3 align-middle'>{shownCapital}</td>
        <td className='py-3 px-3 align-middle'>{member.gender}</td>
        <td className='py-3 px-3 align-middle'>{member.dateOfBirth}</td>
        <td className='py-3 px-3 align-middle'>{member.name}</td>
        <td className='py-3 pl-3 pr-6 align-middle text-right'>
          <KebabMenu
            items={[
              {
                label: 'Editar vida',
                onClick: onEdit,
                icon: <Pencil className='w-4 h-4' strokeWidth={1.8} />,
              },
              {
                label: 'Remover vida',
                onClick: onRemove,
                destructive: true,
                icon: <Trash2 className='w-4 h-4' strokeWidth={1.8} />,
              },
            ]}
            disabled={disabled}
            ariaLabel={`Ações da vida ${member.name}`}
          />
        </td>
      </tr>
      {expanded && (
        <tr className='bg-alba-gray-50'>
          <td colSpan={7} className='py-3 px-3'>
            <CoveragesSubTable
              member={member}
              capitalBase={multiplySalary ? shownCapital : undefined}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** Table header cell with a sort indicator icon (visual only; sorting itself
 *  is not wired yet — Phase 2 is read-only). */
function SortableTh({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th className={`px-3 font-bold ${className ?? ''}`}>
      <span className='inline-flex items-center gap-2'>
        {children}
        <ArrowUpDown
          className='w-3.5 h-3.5 text-alba-black-500'
          strokeWidth={2}
        />
      </span>
    </th>
  );
}

function StatusDot({ awayFromWork }: { awayFromWork: boolean }) {
  const color = awayFromWork ? 'bg-alba-gray-400' : 'bg-alba-success-green-500';
  return (
    <span className='inline-flex items-center'>
      <span className={`inline-block w-3 h-3 rounded-full ${color}`} />
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CoveragesSubTable — list of enabled coverages applied to a single member,
// with the maximum indemnifiable limit and the participation rate
// ─────────────────────────────────────────────────────────────────────────────

function formatBrl(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function CoveragesSubTable({
  member,
  capitalBase,
}: {
  member: GroupLifeMember;
  capitalBase?: string;
}) {
  const { plan, coverageSchema } = useGroupLifeQuote();

  const lines = useMemo(() => {
    if (!plan || !coverageSchema) return [];

    const memberCapital =
      parseCurrencyInput(capitalBase ?? member.capital) ?? 0;
    const uniformCapital = parseCurrencyInput(plan.uniformCapital ?? '') ?? 0;
    const riskBase =
      memberCapital > 0
        ? memberCapital
        : plan.modality === 'UNIFORM' && uniformCapital > 0
          ? uniformCapital
          : memberCapital;

    const resolved = resolveAll(coverageSchema, plan.coverages, {
      totalMemberCapital: riskBase,
    });

    return coverageSchema.coverages
      .filter(c => plan.coverages[c.code]?.enabled)
      .map(c => {
        const sel = plan.coverages[c.code];
        const r = resolved[c.code];
        const cap = r?.resolvedCapital;

        let participation = '—';
        if (
          c.sumInsuredDefinition === 'PROPORTIONAL_TO_COVERAGE' ||
          c.sumInsuredDefinition === 'PROPORTIONAL_TO_RISK'
        ) {
          const pct =
            sel?.kind === c.sumInsuredDefinition &&
            (sel.kind === 'PROPORTIONAL_TO_COVERAGE' ||
              sel.kind === 'PROPORTIONAL_TO_RISK') &&
            sel.proportionOverride != null
              ? sel.proportionOverride
              : c.proportion;
          participation = `${formatPercentValue(pct)}%`;
        }

        let maxLimit = '—';
        switch (c.sumInsuredDefinition) {
          case 'BENEFIT_SELECTION': {
            const childCode =
              sel?.kind === 'BENEFIT_SELECTION'
                ? sel.selectedChildCoverageCode
                : null;
            const child = c.children.find(ch => ch.code === childCode);
            if (cap != null && cap > 0) maxLimit = formatBrl(cap);
            else if (child?.sumInsured != null)
              maxLimit = formatBrl(child.sumInsured);
            else maxLimit = child?.name ?? '—';
            break;
          }
          case 'BENEFIT_SELECTION_WITH_SI': {
            const s =
              sel?.kind === 'BENEFIT_SELECTION_WITH_SI' ? sel : undefined;
            const child = c.children.find(
              ch => ch.code === s?.selectedChildCoverageCode
            );
            return {
              code: c.code,
              label: c.name,
              // Capital digitado pelo corretor; participação = opção escolhida.
              maxLimit: s?.capital != null ? formatBrl(s.capital) : '—',
              participation: child?.name ?? '—',
            };
          }
          case 'EXCLUSIVE_GROUP':
            maxLimit = c.exclusionGroupCode;
            return {
              code: c.code,
              label: c.name,
              maxLimit: c.exclusionGroupCode,
              participation: '—',
            };
          default:
            if (cap != null && cap > 0) maxLimit = formatBrl(cap);
            break;
        }

        return {
          code: c.code,
          label: c.name,
          maxLimit,
          participation,
        };
      });
  }, [plan, coverageSchema, member.capital, capitalBase]);

  if (lines.length === 0) {
    return (
      <Text className='text-xs text-alba-gray-500 italic'>
        Nenhuma cobertura habilitada no Plano (Etapa 2).
      </Text>
    );
  }

  return (
    <div className='overflow-x-auto rounded-2xl border border-alba-gray-200 bg-white'>
      <table
        className='w-full text-sm'
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr className='text-left text-xs uppercase tracking-wider text-[#1A1A1A]'>
            <SortableTh className='pl-6 py-4 bg-[#EFEFEF] rounded-tl-2xl'>
              Coberturas
            </SortableTh>
            <SortableTh className='py-4 bg-[#EFEFEF] text-right'>
              Limite Máx. Indenizável
            </SortableTh>
            <SortableTh className='pr-6 py-4 bg-[#EFEFEF] text-right rounded-tr-2xl'>
              Participação
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {lines.map(l => (
            <tr
              key={l.code}
              className='border-b border-alba-gray-100 last:border-0'
            >
              <td className='py-3 pl-6 pr-3 align-middle'>{l.label}</td>
              <td className='py-3 px-3 align-middle text-right'>
                {l.maxLimit}
              </td>
              <td className='py-3 pl-3 pr-6 align-middle text-right'>
                {l.participation}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KebabMenu — 3-dot vertical menu with destructive support and outside-click
// close
// ─────────────────────────────────────────────────────────────────────────────

type KebabMenuItem = {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  icon?: React.ReactNode;
};

function KebabMenu({
  items,
  disabled,
  ariaLabel,
}: {
  items: KebabMenuItem[];
  disabled?: boolean;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const MENU_WIDTH = 180;

  // Posiciona o menu via `fixed` (portal) a partir do retângulo do botão —
  // assim ele NÃO é cortado pelo container `overflow` da grid. Abre para baixo;
  // se não couber até o fim da viewport, abre para cima.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const estimatedHeight = items.length * 40 + 8;
      const top =
        r.bottom + 4 + estimatedHeight > window.innerHeight
          ? Math.max(8, r.top - 4 - estimatedHeight)
          : r.bottom + 4;
      const left = Math.max(8, r.right - MENU_WIDTH);
      setCoords({ top, left });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    // Fecha em scroll/resize para não deixar o menu "flutuando" fora de lugar
    // (posição fixed não acompanha o scroll do container).
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type='button'
        onClick={() => (open ? setOpen(false) : openMenu())}
        disabled={disabled}
        aria-label={ariaLabel}
        className='p-1 rounded cursor-pointer hover:bg-alba-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
      >
        <MoreVertical
          className='w-4 h-4 text-alba-gray-600'
          strokeWidth={1.8}
        />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              minWidth: MENU_WIDTH,
            }}
            className='z-50 rounded-md border border-alba-gray-200 bg-white shadow-lg'
          >
            {items.map((item, i) => (
              <button
                key={i}
                type='button'
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(h => (h === i ? null : h))}
                onClick={() => {
                  setOpen(false);
                  setHovered(null);
                  item.onClick();
                }}
                style={{
                  backgroundColor:
                    hovered === i
                      ? item.destructive
                        ? '#FEF2F2'
                        : '#F3F4F6'
                      : 'transparent',
                  transition: 'background-color 120ms ease',
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm cursor-pointer ${
                  item.destructive
                    ? 'text-alba-error-red-500'
                    : 'text-alba-black-500'
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pagination — compact page navigator (≤ 5 visible buttons + ellipsis)
// ─────────────────────────────────────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (next: number) => void;
}) {
  const pages = useMemo(
    () => buildPageList(page, totalPages),
    [page, totalPages]
  );

  const go = (n: number) => {
    if (n < 1 || n > totalPages || n === page) return;
    onPageChange(n);
  };

  return (
    <div className='flex items-center gap-1'>
      <button
        type='button'
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label='Página anterior'
        className='w-8 h-8 rounded-md border border-alba-gray-200 text-alba-gray-700 hover:bg-alba-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center'
      >
        <ChevronLeft className='w-4 h-4' strokeWidth={1.8} />
      </button>
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span
            key={`e-${i}`}
            className='w-8 h-8 flex items-center justify-center text-alba-gray-500'
          >
            …
          </span>
        ) : (
          <button
            key={p}
            type='button'
            onClick={() => go(p)}
            className={`w-8 h-8 rounded-md text-sm font-semibold transition-colors ${
              p === page
                ? 'bg-alba-green-500 text-white'
                : 'text-alba-gray-700 hover:bg-alba-gray-50 border border-transparent'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        type='button'
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        aria-label='Próxima página'
        className='w-8 h-8 rounded-md border border-alba-gray-200 text-alba-gray-700 hover:bg-alba-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center'
      >
        <ChevronRight className='w-4 h-4' strokeWidth={1.8} />
      </button>
    </div>
  );
}

function buildPageList(
  page: number,
  totalPages: number
): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  // Always show first; ellipsis; pages around current; ellipsis; last
  const result: (number | 'ellipsis')[] = [];
  result.push(1);
  if (page > 3) result.push('ellipsis');
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  for (let i = start; i <= end; i++) result.push(i);
  if (page < totalPages - 2) result.push('ellipsis');
  result.push(totalPages);
  return result;
}
